import type { AuthAdapter, UniversalUser } from '@/app/_auth/auth-adapter'
import { AuthProviderID } from '@/app/_auth/steps/_common/login-buttons'
import { getApp, initializeApp, type FirebaseApp } from 'firebase/app'
import {
  OAuthProvider,
  linkWithCredential as firebaseLinkWithCredential,
  signOut as firebaseSignOut,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  type Auth,
  type User,
} from 'firebase/auth'

let firebaseApp: FirebaseApp | null = null
const FIREBASE_APP_NAME = '[DEFAULT]'

/**
 * Get or initialize Firebase app (ensures single initialization)
 */
function getOrInitializeFirebaseApp(): FirebaseApp {
  if (firebaseApp) {
    return firebaseApp
  }

  try {
    firebaseApp = getApp(FIREBASE_APP_NAME)
  } catch {
    firebaseApp = initializeApp(
      {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      },
      FIREBASE_APP_NAME,
    )
  }

  return firebaseApp
}

/**
 * Firebase authentication adapter
 */
export class FirebaseAuthAdapter implements AuthAdapter {
  private readonly auth: Auth

  constructor() {
    const app = getOrInitializeFirebaseApp()
    this.auth = getAuth(app)
  }

  /**
   * Get the Firebase Auth instance (for backward compatibility)
   */
  getAuth(): Auth {
    return this.auth
  }

  async initialize(): Promise<void> {
    // Firebase is initialized in constructor, no additional setup needed
  }

  async signIn(providerId: AuthProviderID): Promise<void> {
    try {
      const provider = new OAuthProvider(providerId)
      provider.setCustomParameters({ prompt: 'select_account' })
      await signInWithPopup(this.auth, provider)
    } catch (error) {
      throw error
    }
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth)
    } catch (error) {
      throw error
    }
  }

  getUserId(): string | null {
    return this.auth.currentUser?.uid ?? null
  }

  async getToken(): Promise<string> {
    try {
      await this.authStateReady()
      if (!this.auth.currentUser) {
        const error = new Error('LOGGED_IN_USER_NOT_FOUND')
        throw error
      }
      const token = await this.auth.currentUser.getIdToken()
      if (!token) {
        const error = new Error('FAILED_TO_GET_ACCESS_TOKEN')
        throw error
      }
      return token
    } catch (error) {
      throw error
    }
  }

  getCurrentUser(): User | null {
    return this.auth.currentUser
  }

  async authStateReady(): Promise<void> {
    await this.auth.authStateReady()
  }

  onAuthStateChanged(callback: (user: UniversalUser | null) => void): () => void {
    return onAuthStateChanged(this.auth, (firebaseUser) => {
      const universalUser = firebaseUser
        ? {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          }
        : null

      callback(universalUser)
    })
  }

  isAuthenticated(): boolean {
    return this.auth.currentUser !== null
  }

  async linkWithCredential(user: User, credential: unknown): Promise<void> {
    try {
      await firebaseLinkWithCredential(user, credential as never)
    } catch (error) {
      throw error
    }
  }

  getCredentialFromError(error: unknown): unknown | null {
    return OAuthProvider.credentialFromError(error as never)
  }
}
